<template>
    <div id="segments-members-page">
        <div class="container-fluid">
            <div class="segments-form-container">
                <confirm
                    id="segment-membership-confirm"
                    ref="confirmBatch"
                    :button-class="operation === 'add' ? 'btn-success' : 'btn-danger'"
                    @confirm="executeBatch"
                >
                    <template slot="title">{{ confirmTitle }}</template>
                    <template slot="body">{{ confirmBody }}</template>
                </confirm>

                <loader v-if="is('loading')"></loader>

                <form @submit.prevent v-if="is('ready')">
                    <div class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                        <div>
                            <h1 class="page-title mb-1">{{ pageTitle }}</h1>
                            <p class="text-muted mb-0">{{ segment.name }}</p>
                        </div>
                    </div>

                    <div class="segment-card filters-card">
                        <div class="segment-card-header">
                            <div class="header-title flex-shrink-0 mr-4">
                                <div class="d-flex">
                                    <div class="total-audience">
                                        <span class="far fa-users"></span>
                                        {{ t("segments-form-audience") }}
                                        <strong v-if="!loadingTarget" class="audience-count">{{
                                            estimatedTarget
                                        }}</strong>
                                        <strong v-else>
                                            <span class="far fa-spin fa-spinner"></span>
                                        </strong>
                                    </div>
                                </div>
                            </div>
                            <div :class="'ai-filter-generation ' + (usingAI ? 'active' : '')">
                                <input
                                    ref="ai-input"
                                    type="text"
                                    class="form-control"
                                    v-model="editedDescription"
                                    @blur="handleAiBlur"
                                    :placeholder="t('segments-form-ai_placeholder')"
                                    id="membership-ai-description"
                                    name="membership-ai-description"
                                />
                                <button
                                    class="btn btn-ai flex-shrink-0"
                                    :disabled="(usingAI && !editedDescription) || loadingFilters || processing"
                                    @click="useAI()"
                                >
                                    <span>{{ usingAI ? t('segments-form-ai_filter') : t('segments-form-ai_use') }}</span>
                                </button>
                            </div>
                        </div>

                        <div
                            v-if="filterGenerationSuccess === false"
                            class="alert alert-warning h-auto"
                        >
                            {{ t('segments-form-ai_error_prefix') }} <br />
                            <strong>{{ t('segments-form-ai_error_hint') }}</strong> {{ filterGenerationMessage }}
                        </div>

                        <div class="filter-section">
                            <filter-group
                                @change="changedFilters"
                                ref="filter-bar"
                                :update-query-string="false"
                                :has-branch-filter="true"
                                :has-period-filter="false"
                                :is-vertical="true"
                                :allow-filter-all-places="false"
                                :show-labels="true"
                                :enable-grouping="true"
                                :group="loadingFilters ? emptyGroup : filters"
                                :filter-options="extraFilters"
                                :key="filterGroupKey"
                            >
                                <div slot="empty-state" class="d-flex justify-content-center">
                                    <div v-if="loadingFilters" class="text-center w-100 d-flex flex-column align-items-center">
                                        <sentence-swapper :sentences="loadingSentences" />
                                        <div class="mt-4" style="width:80%; max-width: 400px;">
                                            <ai-loader :height="12" :duration="30000" />
                                        </div>
                                    </div>
                                    <div v-else class="text-center w-100 d-flex flex-column align-items-center">
                                        <span class="text-lg">{{ t("segments-form-empty_state") }}</span>
                                    </div>
                                </div>
                            </filter-group>
                        </div>

                        <div>
                            <small>
                                {{
                                    loadingTarget
                                        ? t("segments-form-calculating_target")
                                        : segmentDescription
                                }}
                            </small>
                        </div>
                    </div>

                    <div class="actions-container">
                        <button
                            type="button"
                            class="btn btn-secondary mr-2"
                            :disabled="processing"
                            @click="$router.push('/segments')"
                        >
                            {{ t("segments-create_modal-cancel") }}
                        </button>
                        <button
                            type="button"
                            :class="operation === 'add' ? 'btn btn-success btn-save-segment' : 'btn btn-danger'"
                            :disabled="processing || loadingTarget || loadingFilters || estimatedTarget === 0"
                            @click="confirmBatch"
                        >
                            <span v-if="!processing">{{ submitLabel }}</span>
                            <span v-else>
                                <i class="fas fa-spinner fa-spin"></i>
                                {{ processingLabel }}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import "./register-filter-components";
import { FilterGroup } from "./register-filter-components";
import { emptyGroup, buildSegmentExtraFilters } from "./segment-filter-options";
import SentenceSwapper from "../components/ui/sentence-swapper.vue";
import AiLoader from "../components/ui/ai-loader.vue";
import { mapState } from "vuex";

export default {
    props: {
        operation: {
            type: String,
            required: true,
            validator(value) {
                return ["add", "remove"].includes(value);
            },
        },
    },
    components: {
        FilterGroup,
        SentenceSwapper,
        AiLoader,
    },
    data: () => ({
        state: "loading",
        segment: null,
        filters: { ...emptyGroup },
        emptyGroup: { ...emptyGroup },
        extraFilters: [],
        fields: [],
        estimatedTarget: 0,
        segmentDescription: "",
        loadingTarget: false,
        previousRequest: null,
        processing: false,
        confirmTitle: "",
        confirmBody: "",
        filterGroupKey: 0,
        usingAI: false,
        editedDescription: "",
        loadingFilters: false,
        filterGenerationMessage: "",
        filterGenerationSuccess: null,
    }),
    computed: {
        ...mapState("placeDetails", ["users"]),
        placeId() {
            return this.$store.state.place.id;
        },
        pageTitle() {
            if (this.operation === "add") {
                return this.t("segments-members-modal_add_title");
            }
            return this.t("segments-members-modal_remove_title");
        },
        submitLabel() {
            if (this.operation === "add") {
                return this.t("segments-members-add_button");
            }
            return this.t("segments-members-remove_button");
        },
        processingLabel() {
            if (this.operation === "add") {
                return this.t("segments-members-saving_add");
            }
            return this.t("segments-members-saving_remove");
        },
        loadingSentences() {
            return [
                this.t("segments-form-loading_sentences-s1"),
                this.t("segments-form-loading_sentences-s2"),
                this.t("segments-form-loading_sentences-s3"),
                this.t("segments-form-loading_sentences-s4"),
                this.t("segments-form-loading_sentences-s5"),
                this.t("segments-form-loading_sentences-s6"),
                this.t("segments-form-loading_sentences-s7"),
                this.t("segments-form-loading_sentences-s8"),
                this.t("segments-form-loading_sentences-s9"),
                this.t("segments-form-loading_sentences-s10"),
                this.t("segments-form-loading_sentences-s11"),
            ];
        },
    },
    async mounted() {
        await this.loadFields();
        await this.resetFilters();
        await this.loadSegment();
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        is(state) {
            return this.state === state;
        },
        setState(state) {
            this.state = state;
        },
        async loadFields() {
            let options = keviApi.getOptions("/client-fields", "GET");
            const response = await this.$http(options);
            this.fields = JSON.parse(JSON.stringify(response.body));
        },
        async resetFilters() {
            this.extraFilters = await buildSegmentExtraFilters(this);
        },
        loadSegment() {
            const segmentId = this.$route.params.id;
            let options = keviApi.getOptions("segment/" + segmentId);
            return this.$http(options)
                .then((response) => {
                    const result = response.body;
                    if (result.type !== "manual") {
                        this.$store.dispatch(
                            "notification/error",
                            this.t("segments-members-dynamic_error")
                        );
                        this.$router.push("/segments");
                        return;
                    }
                    this.segment = result;
                    this.estimateTarget(this.filters);
                    this.setState("ready");
                })
                .catch(() => {
                    this.$router.push("/segments");
                });
        },
        handleAiBlur() {
            if (this.editedDescription.trim() === "") {
                this.usingAI = false;
            }
        },
        useAI() {
            if (this.usingAI) {
                if (this.editedDescription.trim() === "") {
                    return;
                }
                this.generateFilters();
            } else {
                this.usingAI = true;
                this.$nextTick(() => {
                    this.$refs["ai-input"].focus();
                });
            }
        },
        generateFilters() {
            this.loadingFilters = true;
            this.filterGenerationMessage = "";
            this.filterGenerationSuccess = null;
            let options = keviApi.getOptions("segments/generate-filters", "POST", {
                description: this.editedDescription,
            });

            this.$http(options)
                .then((response) => {
                    if (response.status === 200) {
                        const result = response.body;

                        if (result.success) {
                            this.filterGenerationSuccess = true;
                            this.filterGenerationMessage = this.t("segments-form-filters_generated_success");

                            this.resetFilters().then(() => {
                                setTimeout(() => {
                                    this.changedFilters(result.filters);
                                    this.filterGroupKey += 1;
                                    this.usingAI = false;
                                }, 100);
                            });
                        } else {
                            this.filterGenerationSuccess = false;

                            if (
                                result.alternativeDescription &&
                                result.alternativeDescription !== this.editedDescription
                            ) {
                                this.filterGenerationMessage = result.alternativeDescription;
                            }
                        }
                    } else {
                        this.filterGenerationSuccess = false;
                        this.filterGenerationMessage = this.t("segments-form-filters_generated_error");
                    }

                    this.loadingFilters = false;
                })
                .catch((error) => {
                    this.filterGenerationSuccess = false;
                    this.filterGenerationMessage =
                        this.t("segments-form-server_error_prefix") + " " + error.message;
                    this.loadingFilters = false;
                });
        },
        changedFilters(filters) {
            if (!filters) {
                return;
            }
            this.filters = filters;
            this.estimateTarget(filters);
        },
        estimateTarget(filters) {
            var options = keviApi.getOptions("segments/estimate-target", "GET", {
                filters,
            });
            options.before = (request) => {
                this.loadingTarget = true;
                if (this.previousRequest) {
                    this.previousRequest.abort();
                }
                this.previousRequest = request;
            };

            this.$http(options)
                .then((response) => {
                    this.estimatedTarget = response.body.target;
                    this.segmentDescription = response.body.description;
                    this.loadingTarget = false;
                })
                .catch((error) => {
                    if (error.status !== 0) {
                        this.loadingTarget = false;
                    }
                });
        },
        confirmBatch() {
            if (this.operation === "add") {
                this.confirmTitle = this.t("segments-members-confirm_add_title");
                this.confirmBody = this.t("segments-members-confirm_add_body", [this.estimatedTarget]);
            } else {
                this.confirmTitle = this.t("segments-members-confirm_remove_title");
                this.confirmBody = this.t("segments-members-confirm_remove_body", [this.estimatedTarget]);
            }
            this.$refs.confirmBatch.open();
        },
        executeBatch() {
            if (!this.segment) {
                return;
            }

            this.processing = true;
            const options = keviApi.getOptions(
                `segments/${this.segment.id}/batch-membership`,
                "POST",
                {
                    operation: this.operation,
                    filters: this.filters,
                }
            );

            this.$http(options)
                .then((response) => {
                    const body = response.body;
                    const key =
                        this.operation === "add"
                            ? "segments-members-add_success"
                            : "segments-members-remove_success";
                    const count = this.operation === "add" ? body.added : body.removed;
                    this.$store.dispatch(
                        "notification/success",
                        this.t(key, [count, body.skipped || 0])
                    );
                    this.processing = false;
                    this.$router.push("/segments");
                })
                .catch((error) => {
                    this.processing = false;
                    const message =
                        error.body && error.body.error
                            ? error.body.error
                            : this.t("segments-members-error");
                    this.$store.dispatch("notification/error", message);
                });
        },
    },
};
</script>

<style lang="scss">
:root {
  --kevi-green: #11875a;
  --kevi-green-light: #e8f4ef;
  --kevi-gray-light: #f7f9fa;
  --kevi-red: #f44336;
  --kevi-green-dark: #0d6a44;
}

.segments-form-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 0;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #333;
}

.segment-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 24px;
  margin-bottom: 24px;

  h2 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #333;
  }

  small {
    display: block;
    color: #6c757d;
    margin-bottom: 16px;
    font-size: 14px;
  }
}

.segment-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  .header-title {
    display: flex;
    align-items: center;

    h2 {
      margin-bottom: 0;
      margin-right: 12px;
    }
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;

    .total-audience {
      margin-top: 16px;
    }
  }
}

.total-audience {
  background-color: var(--kevi-gray-light);
  border-radius: 6px;
  padding: 12px 16px;

  .audience-count {
    color: var(--kevi-green);
  }

  .fa-users {
    color: var(--kevi-green);
    margin-right: 8px;
  }
}

.filter-section {
  margin-top: 24px;
}

.ai-filter-generation {
  border: 1px solid transparent;
  padding: 4px;
  border-radius: 8px;
  width: 100px;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  justify-content: flex-end;

  input {
    border: none;
    opacity: 0;
    position: absolute;

    &:focus {
      outline: none;
      box-shadow: none;
    }
  }

  &.active {
    border-color: #ddd;
    width: 100%;

    input {
      opacity: 1;
      visibility: visible;
      position: relative;
    }
  }
}

.actions-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;

  .btn-save-segment {
    background-color: var(--kevi-green);
    border-color: var(--kevi-green);
    padding: 10px 24px;
    font-weight: 500;
    min-width: 120px;

    &:disabled {
      opacity: 0.5;
    }

    &:hover,
    &:focus {
      background-color: var(--kevi-green-dark);
      border-color: var(--kevi-green-dark);
    }
  }
}

#segment-membership-confirm.modal {
    position: fixed;
    inset: 0;
    z-index: 1100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

#segment-membership-confirm.modal .modal-dialog {
    margin: 0;
    width: 100%;
    max-width: 520px;
}
</style>
